import { onAuthStateChanged, type User } from "firebase/auth";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import { ApiError, apiGet } from "./api";
import {
  completeEmailSignIn,
  getFirebaseAuth,
  requestEmailSignInLink,
  signInWithPassword,
  signOutCurrentUser
} from "./firebase";
import type { Permission, SessionMe, UserProfile } from "../types/api";

type AuthContextValue = {
  firebaseUser: User | null;
  profile: UserProfile | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  hasPermission: (permission: Permission) => boolean;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  requestLoginLink: (email: string) => Promise<void>;
  completeLoginLink: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  devLogin: () => void;
  refreshSession: () => Promise<void>;
};

const DEV_PROFILE: UserProfile = {
  email: "dev.local@mackenzie.br",
  name: "Desenvolvedor Local",
  userType: "Admin",
  affiliation: "CEDUC",
  permissions: ["dashboard", "graficos", "evasao", "questionario", "prova-facil", "usuarios"]
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSession = useCallback(async (user: User | null) => {
    if (!user) {
      setToken(null);
      setProfile(null);
      return;
    }

    const idToken = await user.getIdToken();
    const session = await apiGet<SessionMe>("/api/session/me", idToken);
    setToken(idToken);
    setProfile(session.user);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getFirebaseAuth(), (user) => {
      setLoading(true);
      setFirebaseUser(user);
      if (user) setError(null);
      void loadSession(user)
        .catch(async (err: unknown) => {
          setError(sessionErrorMessage(err));
          setProfile(null);
          setToken(null);

          if (err instanceof ApiError && err.status === 403) {
            setFirebaseUser(null);
            try {
              await signOutCurrentUser();
            } catch {
              // O perfil segue bloqueado mesmo se a limpeza local do Firebase falhar.
            }
          }
        })
        .finally(() => setLoading(false));
    });

    return unsubscribe;
  }, [loadSession]);

  const value = useMemo<AuthContextValue>(() => ({
    firebaseUser,
    profile,
    token,
    loading,
    error,
    hasPermission: (permission) => Boolean(profile?.permissions.includes(permission)),
    loginWithPassword: async (email, password) => {
      setError(null);
      await signInWithPassword(email, password);
    },
    requestLoginLink: async (email) => {
      setError(null);
      await requestEmailSignInLink(email);
    },
    completeLoginLink: async (email) => {
      setError(null);
      await completeEmailSignIn(email);
    },
    logout: async () => {
      await signOutCurrentUser();
      setFirebaseUser(null);
      setProfile(null);
      setToken(null);
      setError(null);
    },
    devLogin: () => {
      if (!import.meta.env.DEV) return;
      setFirebaseUser(null);
      setToken("dev-token");
      setProfile(DEV_PROFILE);
      setError(null);
      setLoading(false);
    },
    refreshSession: async () => {
      setLoading(true);
      try {
        await loadSession(firebaseUser);
      } finally {
        setLoading(false);
      }
    }
  }), [error, firebaseUser, loadSession, loading, profile, token]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth deve ser usado dentro de AuthProvider.");
  return value;
}

function sessionErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) {
    return "Este e-mail não está autorizado ou está inativo. Procure a equipe CEDUC.";
  }
  if (error instanceof ApiError && error.status === 503) {
    return "O serviço de autorização está temporariamente indisponível. Tente novamente em instantes.";
  }
  return error instanceof Error ? error.message : "Falha ao validar o acesso.";
}
